import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { convertTimeValueToTimeString } from "@/modules/duration/timeConverter"
import { cn } from "@/modules/lib/utils"
import { ChronoRecapBloc } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/chronoRecapBloc"
import {
  convertChronoFormikToChronoDraft,
  type ExerciseFormikType,
  type ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import type { SimpleFormik } from "@/typings/formik"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { TabataIcon } from "@images/svgIcons/tabataIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { displayTotalTime } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { useMemo } from "react"

export function ChronoTabataRecap({
  formik,
  className,
  ...props
}: { formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey> } & React.ComponentProps<"div">) {
  const displayChronoTime = useMemo(() => {
    const chronoDraft = convertChronoFormikToChronoDraft(formik.values)

    if (chronoDraft === undefined) return word("chrono.title.no_chrono")

    return displayTotalTime(chronoDraft)
  }, [formik.values])

  const onDeleteChronoClick = () => {
    formik.setFieldValue("chronoTabata", undefined)
    formik.setFieldValue("chrono", undefined)
    formik.setFieldValue("chronoType", undefined)
  }

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      <div className="mb-3 flex flex-row items-start justify-between">
        <TabataIcon color="currentColor" className="text-textOnColor-body" />

        <div className="flex flex-col items-center">
          <Text fontStyle="LS">{word("chrono.title.tabata")}</Text>

          <div className="flex items-center">
            <ChronoIcon color="currentColor" className="text-textDefault-body" />
            <Text className="ml-2" fontStyle="TL">
              {displayChronoTime}
            </Text>
          </div>
        </div>

        <Button variant="ghost" size="iconMd" onClick={onDeleteChronoClick}>
          <TrashIcon color="currentColor" className="text-textDefault-body" width={22} height={22} />
        </Button>
      </div>

      {formik.values.chronoTabata?.trainingTime && (
        <>
          <div className="mb-2 flex items-center justify-between gap-2">
            <ChronoRecapBloc
              setsIcon
              value={`${formik.values.chronoTabata.numberOfSets} ${word("global.score.set.unit", { count: formik.values.chronoTabata.numberOfSets })}`}
            />

            <ChronoRecapBloc
              circleTimeIcon
              value={`${formik.values.chronoTabata.numberOfRounds} ${word("global.score.round.unit", { count: formik.values.chronoTabata.numberOfRounds })}`}
            />
          </div>

          <div className="mb-2 flex items-center justify-between gap-2">
            <ChronoRecapBloc
              playIcon
              value={`${convertTimeValueToTimeString(formik.values.chronoTabata.trainingTime, "00m 00s")} ON`}
            />
            <ChronoRecapBloc
              circleBreakIcon
              value={`${convertTimeValueToTimeString(formik.values.chronoTabata.restTime, "00m 00s")} OFF`}
            />
          </div>

          <ChronoRecapBloc
            circleBreakIcon
            value={`${convertTimeValueToTimeString(formik.values.chronoTabata.restBetweenSetsTime, "00m 00s")} ${word("chrono.settings.label.rest_between_sets_short")}`}
          />
        </>
      )}
    </div>
  )
}
